import { Alert, AnimatedContentLoader, Button, Checkbox, Input, Prompt, Select } from '@components'
import { HeaderBlock } from '@components/HeaderBlock'
import { getDocumentsPageUrl, ReportType, VatPeriod, VAT_PERIOD_OPTIONS } from '@constants'
import {
  PermissionBoundary,
  PermissionSet,
  useBusinessContext,
  useDateRangeContext,
  usePermission
} from '@context'
import {
  deleteVatPeriod,
  fetchVatPeriod,
  invalidateDashboardQueries,
  IVatPeriod,
  unlockVatPeriod,
  updateVatPeriod
} from '@query'
import { formatDate } from '@utils'
import React, { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { IconType } from 'react-icons'
import { FaCalendarAlt, FaCheckCircle, FaFileDownload, FaTrash, FaUnlock } from 'react-icons/fa'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { generateReport } from '../../utils/generateReport'
import { VatPeriodSteps } from './VatPeriodSteps'
import {
  isUnlockButtonDisabled,
  shouldShowUnlockMenuItem,
  shouldShowUnlockReportedWarning
} from './VatPeriodDetails.helpers'
import styled from 'styled-components'
import { useHistory } from 'react-router-dom'

interface Props {
  vatPeriodId: number
  onClose: () => void
}

export const VatPeriodDetails: React.FC<Props> = ({ vatPeriodId, onClose }) => {
  const [t] = useTranslation()
  const queryClient = useQueryClient()
  const history = useHistory()
  const { slug } = useBusinessContext()
  const hasEditPermission = usePermission(PermissionSet.REPORTING_EDIT)

  const [showDelete, setShowDelete] = useState(false)
  const [showUnlock, setShowUnlock] = useState(false)
  const [hasConfirmedUnlock, setHasConfirmedUnlock] = useState(false)
  const [unlockBlockedByAccountingPeriod, setUnlockBlockedByAccountingPeriod] = useState(false)
  const getDefaultValues = useCallback(
    (data?: Partial<IVatPeriod>) => ({
      start_date: data?.start_date ?? '',
      period: data?.period ?? VatPeriod.MONTH
    }),
    []
  )

  const {
    data: vatPeriod,
    error: vatPeriodError,
    refetch
  } = useQuery({
    queryKey: [slug, 'vat_periods', vatPeriodId],
    queryFn: () => fetchVatPeriod({ slug, vatPeriodId }),
    enabled: !!vatPeriodId
  })

  const form = useForm({
    defaultValues: async () => {
      if (!vatPeriodId) {
        return getDefaultValues()
      }

      const { data } = await refetch()
      return getDefaultValues(data)
    }
  })
  const {
    register,
    handleSubmit,
    formState: { isDirty, isSubmitting, isLoading }
  } = form
  React.useEffect(() => {
    if (vatPeriodError) {
      onClose()
    }
  }, [onClose, vatPeriodError])
  const { setRange } = useDateRangeContext()

  const updateMutation = useMutation<IVatPeriod, unknown, Partial<IVatPeriod>>({
    mutationFn: data => updateVatPeriod({ slug, vatPeriodId }, data),
    ...{
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [slug, 'vat_periods'] })
        queryClient.invalidateQueries({ queryKey: [slug, 'vat_periods', vatPeriodId] })
        void invalidateDashboardQueries(queryClient, slug)
      }
    }
  })

  const deleteMutation = useMutation({
    mutationFn: () => deleteVatPeriod({ slug, vatPeriodId }),
    ...{
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [slug, 'vat_periods'] })
        void invalidateDashboardQueries(queryClient, slug)
        setShowDelete(false)
        onClose()
      }
    }
  })
  const unlockMutation = useMutation({
    mutationFn: () => unlockVatPeriod({ slug, vatPeriodId }),
    ...{
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [slug, 'vat_periods'] })
        queryClient.invalidateQueries({ queryKey: [slug, 'vat_periods', vatPeriodId] })
        void invalidateDashboardQueries(queryClient, slug)
        setShowUnlock(false)
        setHasConfirmedUnlock(false)
        setUnlockBlockedByAccountingPeriod(false)
        toast.success(t('vatPage.unlockSuccess'))
      },
      onError: error => {
        if (
          typeof error === 'object' &&
          error !== null &&
          'status' in error &&
          error.status === 423
        ) {
          setUnlockBlockedByAccountingPeriod(true)
          return
        }

        toast.error(t('error.oho'))
      }
    }
  })

  const save = handleSubmit(data => updateMutation.mutate(data))

  const handleSetDateRange = useCallback(() => {
    if (!vatPeriod) return

    setRange({
      key: 'custom',
      date_from: vatPeriod.start_date,
      date_to: vatPeriod.end_date
    })

    history.push(getDocumentsPageUrl(slug))
  }, [history, setRange, slug, vatPeriod])

  const menuItems = useMemo(() => {
    if (!vatPeriod) return []

    const items: {
      name?: string
      Icon?: IconType
      onClick?: () => void
      intent?: any
    }[] = [
      {
        name: t('vatPage.setAsDateFilter'),
        Icon: FaCalendarAlt,
        onClick: handleSetDateRange
      },
      {
        name: t('vatPage.download'),
        Icon: FaFileDownload,
        onClick: () => {
          generateReport({
            business_id: slug,
            type: ReportType.VAT_REPORT,
            columns: [
              {
                date_from: vatPeriod.start_date,
                date_to: vatPeriod.end_date
              }
            ]
          })
        }
      }
    ]

    if (!vatPeriod.is_locked && hasEditPermission) {
      items.push({
        name: t('vatPage.delete'),
        Icon: FaTrash,
        onClick: () => setShowDelete(true),
        intent: 'danger'
      })
    }

    if (shouldShowUnlockMenuItem(vatPeriod.is_locked, hasEditPermission)) {
      items.push({
        name: t('vatPage.unlock'),
        Icon: FaUnlock,
        onClick: () => {
          setHasConfirmedUnlock(false)
          setUnlockBlockedByAccountingPeriod(false)
          setShowUnlock(true)
        }
      })
    }

    return items
  }, [handleSetDateRange, hasEditPermission, slug, t, vatPeriod])

  if (!vatPeriod) return null

  const showLockedAccountingPeriodError =
    vatPeriod.has_locked_accounting_period || unlockBlockedByAccountingPeriod

  return (
    <StyledWrapper data-key={`details-${vatPeriod.id}`}>
      <HeaderBlock
        header={t('vatPage.vatPeriod')}
        subHeader={
          <StyledDateRange>
            {formatDate(vatPeriod.start_date)} - {formatDate(vatPeriod.end_date)}
          </StyledDateRange>
        }
        menuItems={menuItems}
      />

      <ContentLoaderWrapper>
        <AnimatedContentLoader isLoading={isLoading} contentDisplay="flex">
          <Content>
            <AlertsSection>
              {vatPeriod.is_reported && !vatPeriod.is_locked ? (
                <Alert
                  type="success"
                  isVisible={vatPeriod.is_reported}
                  title={t('vatPage.vatPaid')}
                  description={t('vatPage.vatPaidInfo')}
                />
              ) : (
                vatPeriod.is_reported &&
                vatPeriod.is_locked && (
                  <Alert
                    type="success"
                    isVisible={vatPeriod.is_reported}
                    title={t('vatPage.vatPaid')}
                    description={t('vatPage.vatPaidAndLockedInfo')}
                  />
                )
              )}

              <Alert
                type="warning"
                isVisible={
                  vatPeriod.has_ended && !vatPeriod.has_due_passed && !vatPeriod.is_reported
                }
                title={t('vatPage.notReported')}
                description={t('vatPage.notReportedDueDate', {
                  dueDate: formatDate(vatPeriod.due_date)
                })}
              />

              <Alert
                type="error"
                isVisible={vatPeriod.has_due_passed && !vatPeriod.is_reported}
                title={t('vatPage.notReported')}
                description={t('vatPage.notReportedPastDue', {
                  dueDate: formatDate(vatPeriod.due_date)
                })}
              />

              <Alert
                type="warning"
                isVisible={
                  Boolean(vatPeriod.has_eu_sales) &&
                  vatPeriod.is_reported &&
                  !vatPeriod.is_recapitulative_reported
                }
                title={t('vatPage.euRecap.notReported')}
                description={t('vatPage.euRecap.notReportedDueDate', {
                  dueDate: vatPeriod.recapitulative_due_date
                    ? formatDate(vatPeriod.recapitulative_due_date)
                    : ''
                })}
              />
            </AlertsSection>

            <Section>
              <form onSubmit={save}>
                <fieldset disabled={!hasEditPermission}>
                  <Input
                    type="date"
                    label={t('vatPage.startDate')}
                    info={t('vatPage.startDateInfo')}
                    {...register('start_date', { required: true })}
                    disabled={vatPeriod.is_reported}
                  />

                  <Select
                    {...register('period', { required: true, valueAsNumber: true })}
                    label={t('vatPage.duration')}
                    info={t('vatPage.durationInfo')}
                  >
                    {VAT_PERIOD_OPTIONS.map(
                      ({ value, labelKey }) =>
                        value !== VatPeriod.NONE && (
                          <option key={value} value={value}>
                            {t(labelKey)}
                          </option>
                        )
                    )}
                  </Select>

                  <InlineAlert>
                    <Alert
                      type="info"
                      isVisible={isDirty}
                      description={t('vatPage.updatedAfterSave')}
                    />
                  </InlineAlert>

                  <Input
                    type="date"
                    label={t('vatPage.endDate')}
                    info={t('vatPage.endDateInfo')}
                    value={vatPeriod.end_date}
                    disabled={true}
                  />

                  <Input
                    type="date"
                    label={t('vatPage.reportDate')}
                    info={
                      <Trans i18nKey="vatPage.reportDateInfo">
                        <a href="https://www.vero.fi/yritykset-ja-yhteisot/verot-ja-maksut/arvonlisaverotus/ilmoitus-ja-maksuohjeet/" />
                      </Trans>
                    }
                    value={vatPeriod.due_date}
                    disabled={true}
                  />

                  <PermissionBoundary required={PermissionSet.REPORTING_EDIT}>
                    <FormActions>
                      <Button
                        type="submit"
                        intent="success"
                        disabled={!isDirty}
                        showSpinner={isSubmitting}
                        icon={<FaCheckCircle />}
                      >
                        {t('general.save')}
                      </Button>
                    </FormActions>
                  </PermissionBoundary>
                </fieldset>
              </form>
            </Section>

            <PermissionBoundary required={PermissionSet.REPORTING_EDIT}>
              <Section>
                <SectionTitle>{t('vatPage.events')}</SectionTitle>
                <VatPeriodSteps vatPeriod={vatPeriod} />
              </Section>
            </PermissionBoundary>
          </Content>
        </AnimatedContentLoader>
      </ContentLoaderWrapper>

      <Prompt
        isVisible={showDelete}
        title={t('general.areYouSure')}
        description={t('vatPage.areYouSure')}
        onClose={() => setShowDelete(false)}
        buttons={[
          {
            text: t('general.cancel'),
            action: () => setShowDelete(false)
          },
          {
            text: t('vatPage.delete'),
            intent: 'danger',
            action: () => deleteMutation.mutate()
          }
        ]}
      />
      <Prompt
        isVisible={showUnlock}
        title={t('vatPage.unlockPromptTitle')}
        description={
          <UnlockPromptContent>
            <p>{t('vatPage.unlockPromptDescription')}</p>
            {shouldShowUnlockReportedWarning(vatPeriod.is_reported) && (
              <p>{t('vatPage.unlockPromptReportedWarning')}</p>
            )}
            <Checkbox
              checked={hasConfirmedUnlock}
              onChange={event => setHasConfirmedUnlock(event.target.checked)}
              label={t('vatPage.unlockPromptConfirm')}
              id="vat-period-unlock-confirm"
            />
            <Alert
              type="error"
              isVisible={showLockedAccountingPeriodError}
              description={t('vatPage.unlockPromptLockedAccountingPeriodError')}
            />
          </UnlockPromptContent>
        }
        onClose={() => {
          setShowUnlock(false)
          setHasConfirmedUnlock(false)
          setUnlockBlockedByAccountingPeriod(false)
        }}
        buttons={[
          {
            text: t('general.cancel'),
            action: () => {
              setShowUnlock(false)
              setHasConfirmedUnlock(false)
              setUnlockBlockedByAccountingPeriod(false)
            }
          },
          {
            text: t('vatPage.unlock'),
            intent: 'success',
            action: () => unlockMutation.mutate(),
            disabled: isUnlockButtonDisabled(
              hasConfirmedUnlock,
              unlockMutation.isPending,
              showLockedAccountingPeriodError
            ),
            showSpinner: unlockMutation.isPending
          }
        ]}
      />
    </StyledWrapper>
  )
}

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.neutralWhite};
  width: 100%;
  height: 100%;
  overflow: hidden;
`

const ContentLoaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: ${({ theme }) => theme.spacing.md}rem;
  gap: ${({ theme }) => theme.spacing.lg}rem;
  overflow: auto;
`

const AlertsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const InlineAlert = styled.div`
  margin: ${({ theme }) => theme.spacing.xs}rem 0;
`

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing.sm}rem;
`

const SectionTitle = styled.h3`
  margin: 0;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.neutralBlack};
  font-size: ${({ theme }) => theme.fontSize.lg}rem;
`

const StyledDateRange = styled.span`
  display: inline-block;
  color: ${({ theme }) => theme.colors.iconGray};
  font-size: ${({ theme }) => theme.fontSize.md}rem;
`

const UnlockPromptContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`
